import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Alert,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';

const SignInPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signInWithGoogle } = useAuth();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Get returnUrl parameter from URL
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get('returnUrl') || '/user';

  if (user) {
    // Redirect to returnUrl if it exists
    navigate(returnUrl);
    return null;
  }

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsLoading(true);

    try {
      await signInWithGoogle();
      navigate(returnUrl);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ my: 8 }}>
        <Paper sx={{ p: 4 }}>
          <Box sx={{ 
            textAlign: 'center',
            mb: 4,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -16,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '200px',
              height: '2px',
              background: 'linear-gradient(90deg, #EF233C, transparent)',
            }
          }}>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                mb: 2,
                fontFamily: '"Noto Serif JP", serif',
                fontWeight: 500,
              }}
            >
              {t('navigation.signIn')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Typography variant="body1" textAlign="center" sx={{ mb: 3 }}>
            {t('auth.signInWithGoogleOnly')}
          </Typography>

          <Button
            fullWidth
            variant="contained"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            sx={{
              mb: 3,
              bgcolor: '#4285F4',
              color: '#fff',
              '&:hover': {
                bgcolor: '#3367D6',
              },
            }}
          >
            {isLoading ? t('common.loading') : t('auth.signInWithGoogle')}
          </Button>
          
          <Typography variant="body2" textAlign="center" color="text.secondary">
            {t('auth.bySigningIn')}
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default SignInPage; 